import React from 'react';

import Box from '@atlaskit/primitives/box';
import Stack from '@atlaskit/primitives/stack';
import Text from '@atlaskit/primitives/text';
import { xcss } from '@atlaskit/primitives/xcss';

import TableTree from '@atlaskit/table-tree/table-tree';
import { Cell } from '@atlaskit/table-tree/cell';
import { Header } from '@atlaskit/table-tree/header';
import { Headers } from '@atlaskit/table-tree/headers';
import { Row } from '@atlaskit/table-tree/row';
import { Rows } from '@atlaskit/table-tree/rows';

import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import TaskToDoIcon from '@atlaskit/icon/core/task-to-do';

import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading/heading';

import { useIntl, defineMessage } from '@atlassian/jira-intl';
import type {
	HealthcheckEntry,
	HealthcheckStatus,
} from './HealthcheckSuggestedActionLink.types.tsx';
import HealthcheckSuggestedAction from './HealthcheckSuggestedAction.tsx';

const statusHeader = defineMessage({
	id: 'applinks.extra-auth.healthcheck-table.status-header',
	defaultMessage: 'Status',
	description:
		'The text is shown as a table column header for the healthcheck status. Appears in the table header row to label the status column.',
});

const itemHeader = defineMessage({
	id: 'applinks.extra-auth.healthcheck-table.item-header',
	defaultMessage: 'Item',
	description:
		'The text is shown as a table column header for the healthcheck item name. Appears in the table header row to label the item column.',
});

const details = defineMessage({
	id: 'applinks.extra-auth.healthcheck-table.details',
	defaultMessage: 'Details',
	description:
		'The text is shown as a table column header for the healthcheck details or error messages. Appears in the table header row to label the details column.',
});
const HealthcheckIcon = (props: HealthcheckEntry) => {
	return (
		<>
			{props.status.statusType === 'SUCCESS' && (
				<StatusSuccessIcon color={token('color.icon.success')} label="Success" />
			)}
			{props.status.statusType === 'FAILURE' && (
				<CrossCircleIcon color={token('color.icon.danger')} label="Unsuccessful" />
			)}
			{props.status.statusType === 'NOT_TRIED' && (
				<TaskToDoIcon color={token('color.icon')} label="Not Tried" />
			)}
		</>
	);
};

const getColorFromStatus = (status: HealthcheckStatus) => {
	if (status.statusType === 'NOT_TRIED') {
		return 'color.text.disabled';
	}
	return 'color.text';
};

const HealthcheckTitle = (props: HealthcheckEntry) => {
	return <Text color={getColorFromStatus(props.status)}>{props.name}</Text>;
};

const HealthcheckComment = (props: HealthcheckEntry) => {
	const message = 'message' in props.status ? props.status.message : '';
	return <Text color={getColorFromStatus(props.status)}>{message}</Text>;
};

type Item = {
	id: string;
	content: HealthcheckEntry;
	hasChildren: boolean;
	children?: Item[];
};

const convertToArray = (healthcheckEntries: HealthcheckEntry[]): Item[] => {
	const wrapWithItem = (
		id: string,
		content: HealthcheckEntry,
		hasChildren: boolean,
		children?: Item[],
	) => {
		return { id, content, hasChildren, children };
	};

	return healthcheckEntries.map((entry, index) => {
		return wrapWithItem(`entry-${index}`, entry, false);
	});
};

const HealthcheckTable = (props: {
	title: string;
	description: string;
	healthcheckEntries: HealthcheckEntry[] | null;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const healthcheckItems = props.healthcheckEntries
		? convertToArray(props.healthcheckEntries)
		: undefined; // Loading state

	return (
		<Stack space="space.100">
			<Heading size="medium">{props.title}</Heading>
			<Text>{props.description}</Text>
			<TableTree label="Healthcheck Table">
				<Headers>
					<Header width="100px">{formatMessage(statusHeader)}</Header>
					<Header width="50%">{formatMessage(itemHeader)}</Header>
					<Header width="50%">{formatMessage(details)}</Header>
				</Headers>
				<Rows
					items={healthcheckItems}
					render={(item: Item) => {
						const { content } = item;

						const row = (
							<Row key={item.id} itemId={item.id} hasChildren={item.hasChildren}>
								<Cell>
									<HealthcheckIcon {...content} />
								</Cell>
								<Cell>
									<HealthcheckTitle {...content} />
								</Cell>
								<Cell>
									<HealthcheckComment {...content} />
								</Cell>
							</Row>
						);

						if (content.status.statusType === 'FAILURE') {
							return (
								<>
									{row}
									<Row
										key={`${item.id}-suggested-action`}
										itemId={`${item.id}-suggested-action`}
										hasChildren={false}
									>
										<Box xcss={suggestedActionCellStyles} role="cell">
											<HealthcheckSuggestedAction
												error={`Step "${content.name}" failed due to reason: "${
													content.status.message
												}".`}
												action={content.suggestedAction ?? null}
												link={content.suggestedActionLink ?? null}
											/>
										</Box>
									</Row>
								</>
							);
						}

						return row;
					}}
				/>
			</TableTree>
		</Stack>
	);
};

export default HealthcheckTable;
const suggestedActionCellStyles = xcss({ width: '100%', padding: 'space.200' });
